// Shared leadership ordering + row-layout helpers.
//
// These MUST stay in lock-step with the admin dashboard, which writes the
// arrangement this board renders (admindashboard repo:
// src/utils/leadershipLayout.js). Keep the two files interchangeable — the
// admin "Arrange Layout" preview is only WYSIWYG if both sides group the
// roster the same way.
//
// Layout model
// ------------
// Every theme lays the leadership screen out in FOUR rows. Each member carries
// an explicit `row` (which row they live in) and an `order` (their position in
// the flat, row-major reading of the whole screen). Rows are therefore a real
// assignment, not a side effect of how many people happen to be above someone.
//
// Older boards were laid out by slicing the flat ordered list at fixed index
// boundaries. That model could not express "row 2 holds two people and row 3
// holds one" — the slice always refilled earlier rows first. Members saved
// before that change have no `row` field; they are resolved through the old
// slice boundaries (`start`/`end` below) so existing arrangements render
// exactly as they did.

// Normalize a numeric-ish field (number or numeric string) to a number, else null.
const numLevel = (v) => {
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
};

// Infer a rank tier from the title when no explicit level is set.
const inferLevel = (title) => {
  const t = String(title ?? '').toLowerCase();
  if (t.includes('chief')) return /\b(asst|assistant|deputy)\b/.test(t) ? 1 : 0;
  if (t.includes('captain') || /\bcapt\b/.test(t)) return 2;
  if (t.includes('lieutenant') || /\b(lt|lieut)\b/.test(t)) return 3;
  return null;
};

const effLevel = (l) => {
  const lv = numLevel(l.level);
  return lv != null ? lv : inferLevel(l.title);
};

// Order by explicit display `order` when present, otherwise by rank tier so the
// highest ranks come first; unknowns sort last. NaN-safe.
export const leadershipSortVal = (l) => {
  const o = numLevel(l.order);
  if (o != null) return o;
  const lv = effLevel(l);
  return lv != null ? lv : 99;
};

// Return a new array sorted into the exact display order the board uses.
export const orderLeadership = (list = []) =>
  [...list].sort((a, b) => leadershipSortVal(a) - leadershipSortVal(b));

// Per-theme row layout. All themes offer four rows.
//   `size`       drives card sizing.
//   `capacity`   how many cards the row is designed to hold (the last row takes
//                whatever is left over). A guide, not a limit — the admin can
//                place anyone in any row.
//   `start`/`end` legacy slice boundaries, kept only to place members saved
//                before rows became explicit.
//   classicRed:                 3 across, 3 across, 3 across, then the rest.
//   hierarchy (blue/dark/light): 1 (large), 3 (medium), 6 (normal), then rest.
const THEME_ROW_LAYOUT = {
  classicRed: [
    { size: 'normal', capacity: 3, start: 0, end: 3 },
    { size: 'normal', capacity: 3, start: 3, end: 6 },
    { size: 'normal', capacity: 3, start: 6, end: 9 },
    { size: 'normal', capacity: Infinity, start: 9, end: Infinity },
  ],
  hierarchy: [
    { size: 'large', capacity: 1, start: 0, end: 1 },
    { size: 'medium', capacity: 3, start: 1, end: 4 },
    { size: 'normal', capacity: 6, start: 4, end: 10 },
    { size: 'normal', capacity: Infinity, start: 10, end: Infinity },
  ],
};

export const getThemeRowLayout = (theme) =>
  theme === 'classicRed' ? THEME_ROW_LAYOUT.classicRed : THEME_ROW_LAYOUT.hierarchy;

// How many rows a theme exposes. Every theme has four.
export const getThemeRowCount = (theme) => getThemeRowLayout(theme).length;

// Coerce a stored `row` to a usable row index, clamped into range. Returns null
// when the member has no explicit row (legacy data).
export const normalizeRowIndex = (value, rowCount) => {
  const n = numLevel(value);
  if (n == null) return null;
  const i = Math.trunc(n);
  if (i < 0) return 0;
  if (i > rowCount - 1) return rowCount - 1;
  return i;
};

// Where the old index-slice model would have put the member at `index`.
const legacyRowForIndex = (layout, index) => {
  const found = layout.findIndex((r) => index >= r.start && index < r.end);
  return found === -1 ? layout.length - 1 : found;
};

// Resolve the row index for every member of an already-ordered roster.
// Explicit `row` wins; anything without one falls back to the legacy slice.
export const resolveLeadershipRows = (ordered = [], theme) => {
  const layout = getThemeRowLayout(theme);
  return ordered.map((item, index) => {
    const explicit = normalizeRowIndex(item?.row, layout.length);
    return explicit != null ? explicit : legacyRowForIndex(layout, index);
  });
};

// Group an already-ordered roster into the theme's rows for rendering.
// Returns: [{ index, size, capacity, items: [...] }]. Members keep their
// relative order inside each row, and every row is always present (callers skip
// the empty ones so the screen stays tight).
export const sliceLeadershipRows = (ordered = [], theme) => {
  const layout = getThemeRowLayout(theme);
  const rowOf = resolveLeadershipRows(ordered, theme);
  const buckets = layout.map(() => []);
  ordered.forEach((item, index) => {
    buckets[rowOf[index]].push(item);
  });
  return layout.map((row, index) => ({
    index,
    size: row.size,
    capacity: row.capacity,
    items: buckets[index],
  }));
};
